import { FormGroup } from '@angular/forms';
import moment from 'moment';
import {
  OpzioniNaturaIva,
  PaymentType,
  StsExpenseLine,
  StsExpensePayload,
} from './sts-models';

function parseNaturaIvaEnum(
  code: string | null | undefined
): OpzioniNaturaIva | null {
  if (!code) {
    return null;
  }
  const map: Record<string, OpzioniNaturaIva> = {
    N1: OpzioniNaturaIva.N1,
    'N2.1': OpzioniNaturaIva.N2_1,
    'N2.2': OpzioniNaturaIva.N2_2,
    N4: OpzioniNaturaIva.N4,
  };
  return map[code] ?? null;
}

function mapDocumentLine(line: Record<string, unknown>): StsExpenseLine {
  const ivaOption = String(line['iva_option'] ?? '');
  let iva: number;
  let natura_iva: OpzioniNaturaIva | null;

  if (ivaOption.includes('-')) {
    iva = 0;
    natura_iva =
      parseNaturaIvaEnum(ivaOption.split('- ')[1]) ?? OpzioniNaturaIva.N1;
  } else {
    iva = parseInt(ivaOption.replace('%', ''), 10) || 0;
    natura_iva = null;
  }

  return {
    descrizione: String(line['descrizione'] ?? ''),
    quantita: parseFloat(String(line['quantita'] ?? '0')),
    prezzo: parseFloat(String(line['prezzo'] ?? '0')),
    iva,
    natura_iva,
    totale: parseFloat(String(line['totale'] ?? '0')),
  };
}

/** Maps the STS reactive form to `StsExpensePayload` for the invoicing API. */
export function buildStsInvoicePayload(
  invoiceForm: FormGroup
): StsExpensePayload {
  const formValue = invoiceForm.getRawValue();
  const { cassa_previdenza_codice_iva } = formValue;

  let cassa_previdenza_iva = 0;
  let cassa_previdenza_natura_iva: OpzioniNaturaIva | null = null;

  if (formValue.cassa_previdenza && cassa_previdenza_codice_iva) {
    if (String(cassa_previdenza_codice_iva).includes('-')) {
      cassa_previdenza_iva = 0;
      cassa_previdenza_natura_iva =
        parseNaturaIvaEnum(
          String(cassa_previdenza_codice_iva).split('- ')[1]
        ) ?? OpzioniNaturaIva.N1;
    } else {
      cassa_previdenza_iva =
        parseInt(String(cassa_previdenza_codice_iva).replace('%', ''), 10) || 0;
      cassa_previdenza_natura_iva = null;
    }
  }

  const data = formValue.data
    ? moment(formValue.data).format('DD/MM/YYYY')
    : formValue.data;

  return {
    tipo: 'Parcella Privati',
    client_id: formValue.client_id,
    riferimento: formValue.riferimento ?? '',
    progressivo_numero: `${formValue.progressivo_numero}`,
    progressivo_anno: Number(formValue.progressivo_anno),
    data,
    pagata: !!formValue.pagata,
    scadenza: formValue.scadenza,
    pagamento: formValue.pagamento as PaymentType,
    cassa_previdenza: !!formValue.cassa_previdenza,
    tipologia_cassa: formValue.tipologia_cassa,
    percentuale_cassa: parseFloat(formValue.percentuale_cassa ?? '0'),
    cassa_previdenza_iva,
    cassa_previdenza_natura_iva,
    applica_bollo: !!formValue.applica_bollo,
    linee_documento: (formValue.linee_documento ?? []).map(
      (line: Record<string, unknown>) => mapDocumentLine(line)
    ),
    imponibile: parseFloat(formValue.imponibile ?? '0'),
    totale_cassa_previdenza: parseFloat(
      formValue.totale_cassa_previdenza ?? '0'
    ),
    imposta: parseFloat(formValue.imposta ?? '0'),
    bollo: parseFloat(formValue.bollo ?? '0'),
    totale: parseFloat(formValue.totale ?? '0'),
    doctor_bank_name: formValue.doctor_bank_name ?? null,
    doctor_bank_iban: formValue.doctor_bank_iban ?? null,
    flag_opposizione: !!formValue.flag_opposizione,
    escludi_bollo: !!formValue.escludi_bollo,
  };
}
